import styled from '@emotion/styled';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {
  SmallTypography,
  StandardSemiBoldTypography,
  StandardTypography,
} from 'patient-ping-remedy/packages/typography';

/**
 * Content-only layout for the pre-review wizard. The progress rail is rendered by the host
 * (TaskEditProgress) — this component only renders the active step body (CARECO-6078).
 * `padding-bottom` keeps the last section (Reason for referral / confirm banner) off the
 * viewport edge so it is not clipped when the content scrolls.
 */
export const Content = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 3rem;
  min-width: 0;
`;

export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const NavButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SectionTitle = styled.h3`
  margin: 24px 0 8px;
`;

/* Reuse Remedy Input's own required/error primitives so the Textarea's asterisk and error match
 * Input visually (Input renders Required via StandardSemiBoldTypography/red5 and the error via
 * SmallTypography/yellow5). */
export const RequiredMark = styled(StandardSemiBoldTypography)`
  color: ${colors.red5};
`;

export const ErrorText = styled(SmallTypography)`
  display: block;
  margin: ${sizes.xxSmall} 0;
  color: ${colors.yellow5};
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/* gray2 (#E5E5E5) is a light hairline border; the previous gray5 (#5C595C) rendered a heavy dark border. */
export const Card = styled.div`
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* Figma-style contact line: an icon followed by its value (phone, email), aligned on one row. */
export const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.gray5};
`;

/* Wraps a Badge so it sizes to its content (a pill) instead of stretching to the full width of the
 * flex-column card (SummaryBlock/CardBody default to align-items: stretch). */
export const BadgeRow = styled.div`
  display: flex;
`;

/* Figma two-column card (CARECO-6363): identity (name / org / specialty badge) on the left,
 * contact details (phone / email with icons) on the right. */
export const ProviderCard = styled.div`
  background: ${colors.gray1};
  border-radius: 8px;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

export const CardIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CardName = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.charcoal};
`;

/* Owning-organization line under a card name (CARECO-6369). */
export const CardOrg = styled.div`
  font-size: 0.875rem;
  color: ${colors.gray5};
`;

export const CardContact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* Right-hand group of a card: contact details plus an optional action (e.g. remove), so the
 * identity stays left and contact/action sit together on the right (CARECO-6363). */
export const CardRight = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const EmptyState = styled.div`
  border: 1px dashed ${colors.gray3};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${colors.gray4};
`;

/*
 * Create-step summary cards. gray1 (#F2F2F2) is the lightest gray; the previous gray5 (#5C595C)
 * rendered these cards near-black with unreadable text (CARECO-6078, "sections displaying in black").
 */
export const SummaryBlock = styled.div`
  background: ${colors.gray1};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${sizes.xSmall};
`;

export const NameRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${sizes.xLarge};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

export const FieldLabel = styled(StandardSemiBoldTypography)``;

export const FieldValue = styled(StandardTypography)``;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 8px 12px;
  border: 1px solid ${colors.gray3};
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
`;

/*
 * Remedy's Banner only renders {children, type, className}. The Create-gate banner lists the
 * specific unmet confirm-gate conditions as a compact bullet list (CARECO-6086).
 */
export const UnmetList = styled.ul`
  margin: 4px 0 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
